export type FAQPair = { question: string; answer: string };

/**
 * Build FAQPage JSON-LD for Google Rich Results from generic Q&A pairs.
 * Returns null when no valid Q&A pairs are found.
 */
export function buildFaqJsonLd(
  pairs: FAQPair[] | undefined | null,
  pageName?: string
): Record<string, unknown> | null {
  if (!pairs || pairs.length === 0) return null;

  const valid = pairs
    .map((p) => ({
      question: (p?.question ?? '').toString().trim(),
      answer: (p?.answer ?? '').toString().trim(),
    }))
    .filter((x) => x.question.length > 0 && x.answer.length > 0);

  if (valid.length === 0) return null;

  const mainEntity = valid.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  }));

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };

  if (pageName && pageName.trim().length > 0) {
    jsonLd.name = pageName.trim();
  }

  return jsonLd;
}

// Common JSON-LD helper: Breadcrumbs
export type BreadcrumbPair = { name: string; url?: string };

function normalizeBaseUrl(value: unknown): string | undefined {
  const trimmed = (value ?? '').toString().trim();
  if (!trimmed) return undefined;

  try {
    const parsed = new URL(trimmed);
    return `${parsed.origin}/`;
  } catch {
    return undefined;
  }
}

export function toAbsoluteUrl(value: unknown, baseUrl?: string): string {
  const trimmed = (value ?? '').toString().trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('http')) {
    // Keep already-absolute URLs as-is.
    try {
      // eslint-disable-next-line no-new
      new URL(trimmed);
      return trimmed;
    } catch {
      // fall through
    }
  }
  const baseUrlToUse = baseUrl || process.env.PUBLIC_URL || '';

  if (!baseUrlToUse) return trimmed;

  try {
    return new URL(trimmed, baseUrlToUse).toString();
  } catch {
    return trimmed;
  }
}

/**
 * Build BreadcrumbList JSON-LD for Google/Schema.org.
 * - The last item is emitted without `item` (represents current page).
 * - Earlier items include `item` URLs (absolute when `baseUrl`/`PUBLIC_URL`/browser origin is available).
 */
export function buildBreadcrumbJsonLd(
  items: BreadcrumbPair[] | undefined | null,
  baseUrl?: string
): Record<string, unknown> | null {
  if (!items || items.length === 0) return null;

  const resolvedBaseUrl =
    normalizeBaseUrl(baseUrl) ||
    normalizeBaseUrl(process.env.PUBLIC_URL) ||
    (typeof window !== 'undefined' ? normalizeBaseUrl(window.location?.origin) : undefined);

  const cleaned = items
    .map((x) => ({
      name: (x?.name ?? '').toString().trim(),
      url: (x?.url ?? '').toString().trim(),
    }))
    .filter((x) => x.name.length > 0);

  if (cleaned.length === 0) return null;

  const itemListElement = cleaned.map((x, idx) => {
    const base: Record<string, unknown> = {
      '@type': 'ListItem',
      position: idx + 1,
      name: x.name,
    };
    if (x.url) {
      const absoluteUrl = toAbsoluteUrl(x.url, resolvedBaseUrl);
      if (absoluteUrl) {
        base.item = absoluteUrl;
      }
    }
    return base;
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  } as const;
}
